import xor from 'lodash/xor'
import React, { FC, useState, useEffect } from 'react'
import cx from 'classnames'
import isEmpty from 'lodash/isEmpty'
import { Button, createTimeOptions } from '@ui'
import { LocationWorkingHoursGroupI } from '@api'
import { useWorkingHoursGroupsT } from '../../hooks/useWorkingHoursLocationGroups'
import TimeSelector from '../../TimeSelector'
import LocationGroupTrees from './components/LocationGroupTrees/LocationGroupTrees'
import { useTreeContents } from './hooks'
import css from './locationGroupsItem.module.scss'
import { getDefaultTimes, to24HourFormatStr } from '@utils'

type PropsT = {
  workingHoursGroupsHook: Omit<useWorkingHoursGroupsT, 'locationWorkingHoursGroups'>
  locationWorkingHoursGroup: LocationWorkingHoursGroupI
}

const LocationGroupsItem: FC<React.PropsWithChildren<PropsT>> = ({
  workingHoursGroupsHook,
  locationWorkingHoursGroup: group,
}) => {
  const {
    regions,
    allSelectedFloorIds,
    updateWorkingHoursGroupById,
    saveWorkingHoursGroupById,
    cancelWorkingHoursGroupEditingById,
    removeWorkingHoursGroupById,
    editWorkingHoursGroupById,
  } = workingHoursGroupsHook

  const [workingTimeFrom, setWorkingTimeFrom] = useState(group.timeFrom)
  const [workingTimeTo, setWorkingTimeTo] = useState(group.timeTo)
  const { timeFrom, timeTo } = getDefaultTimes(workingTimeFrom, workingTimeTo)
  const timezone = 'Etc/UTC'
  const timeList = createTimeOptions(timezone)
  const treeContentsHook = useTreeContents({
    regions,
    disabledFloorIds: xor(allSelectedFloorIds, group.floorIds),
    selectedFloorIds: group.floorIds,
  })

  const updateWorkingHoursGroup = (data?: Partial<LocationWorkingHoursGroupI>) => {
    updateWorkingHoursGroupById(group.key, data)
  }

  const cancel = () => {
    setWorkingTimeFrom(group.timeFrom)
    setWorkingTimeTo(group.timeTo)
    cancelWorkingHoursGroupEditingById(group.key)
    treeContentsHook.resetTreeNodes()
  }

  useEffect(updateWorkingHoursGroup, [JSON.stringify(treeContentsHook.selectedFloorIds)])

  return (
    <>
      <div className="bg-grey-100 p-4">
        <LocationGroupTrees treeContentsHook={treeContentsHook} locationWorkingHoursGroup={group} />
        <div className="flex items-baseline">
          {!group.isBeingEdited ? (
            <div className="text-sm text-grey-700 pt-4">
              Working hours:{' '}
              <b className="font-bold">
                {timeFrom.format('h:mm a')} - {timeTo.format('h:mm a')}
              </b>
            </div>
          ) : (
            <TimeSelector
              timeFrom={timeFrom}
              timeTo={timeTo}
              timezone={timezone}
              timeList={timeList}
              disabledToOptions={timeList.reduce<number[]>(
                (acc, m, i) => (m.isSameOrBefore(timeFrom) ? [...acc, i] : acc),
                []
              )}
              onChange={(value, isFrom) => {
                if (isFrom) {
                  if (value === workingTimeFrom) return
                  setWorkingTimeFrom(to24HourFormatStr(value))
                  updateWorkingHoursGroup()
                } else {
                  if (value === workingTimeTo) return
                  setWorkingTimeTo(to24HourFormatStr(value))
                  updateWorkingHoursGroup()
                }
              }}
            />
          )}
        </div>
      </div>
      <div className={cx('bg-grey-100 p-2 mb-4', css.bottom_settings)}>
        {!group.isBeingEdited ? (
          <div className="flex justify-between">
            <Button
              minimal
              iconName="trash"
              text="DELETE LOCATION GROUP"
              onClick={() => {
                removeWorkingHoursGroupById(group.key)
              }}
            />
            <Button
              minimal
              blue
              iconName="edit"
              text="EDIT"
              onClick={() => {
                editWorkingHoursGroupById(group.key)
              }}
            />
          </div>
        ) : (
          <div className="flex justify-end">
            <Button minimal iconName="remove" text="CANCEL" onClick={cancel} />
            <Button
              minimal
              blue
              loading={group.isLoading}
              disabled={!group.isChanged || isEmpty(treeContentsHook.selectedFloorIds)}
              text="SAVE"
              onClick={() => {
                saveWorkingHoursGroupById(
                  group.key,
                  {
                    floorIds: treeContentsHook.selectedFloorIds,
                    timeFrom: workingTimeFrom,
                    timeTo: workingTimeTo,
                  },
                  cancel
                )
              }}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default LocationGroupsItem
